import { useState } from "react";
import { Tabs, Alert, Button, Space, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { TabsProps } from "antd";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useFinanzasEmpresaLogic } from "./useFinanzasEmpresaLogic";
import { FinanzasSection } from "./finanzasSection";
import "./finanzasEmpresa.css";
import { AseosSection } from "./sectionAseos";
import { LavanderiaSection } from "./sectionLavanderia";
import { ArriendosSection } from "./sectionArriendos";
import { ProductosSection } from "./sectionProductos";
import { ResumenSection } from "./sectionResumen";
import { SectionErrorBoundary } from "./SectionErrorBoundary";
import { CierreMensualCard } from "./CierreMensualCard";
import {
  FiltroGlobalFecha,
  useFiltroFechaPersistido,
} from "./filtroGlobalFecha";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

/**
 * Pestañas válidas HOY. La pestaña activa se recuerda, y un valor guardado por
 * una versión anterior (una pestaña que ya no existe) dejaría la página en
 * blanco: por eso se valida contra esta lista antes de restaurarla.
 * Ojo: la key "gastos" se muestra como "Flujos" (renombre 2026-05-13).
 */
const PESTANAS_FINANZAS_EMPRESA = [
  "resumen",
  "sueldos",
  "gastos",
  "aseos",
  "lavanderia",
  "arriendos",
  "productos",
];

export default function FinanzasEmpresaPage() {
  const {
    sidebarOpen,
    toggleSidebar,
    loading,
    usuarios,
    propiedades,
    sueldos,
    gastos,
    createSueldo,
    updateSueldo,
    deleteSueldo,
    createGasto,
    updateGasto,
    deleteGasto,
    aseos,
    arriendos,
    productos,
    marcas,
    bodegas,
    bodeguitas,
    refetchShared,
  } = useFinanzasEmpresaLogic();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Botón "Actualizar": recalcula todos los totales del período SIN cambiar el
  // mes seleccionado ni la pestaña activa. Cada sección hace su propio fetch
  // (arriendos, aseos, lavandería, etc.); en vez de remontarlas (lo que
  // perdería su estado interno: orden de columnas, filtros, cards expandidas),
  // les pasamos un `refreshKey` que incluyen en las deps de su efecto de carga.
  // Al incrementarlo, todas re-consultan el backend en su lugar. `refetchShared`
  // refresca además los catálogos compartidos (usuarios/propiedades/etc.).
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    try {
      await refetchShared();
    } finally {
      setRefreshing(false);
    }
  };

  // Banner reusable: "para CRUD ir a /<modulo>". Round 2026-04-30 nonies —
  // /finanzas-empresa pasó a ser puro reporte; el CRUD vive en módulos
  // dedicados bajo Gestión en el sidebar.
  const ReadOnlyBanner = ({
    titulo,
    path,
  }: {
    titulo: string;
    path: string;
  }) => (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message={`Esta vista de ${titulo} es solo lectura`}
      description="Es un reporte agregado con totales, filtros y exportación. Para crear, editar o eliminar registros, ir al módulo dedicado."
      action={
        <Space>
          <Button type="primary" onClick={() => navigate(path)}>
            Ir a {titulo}
          </Button>
        </Space>
      }
    />
  );

  // `?from=&to=` es un enlace directo (las barras de /estadisticas → Empresa).
  // Se resuelve como valor INICIAL y no con el setter: el setter marca el
  // filtro como «tocado» y lo guardaría como preferencia del admin, que
  // durante 7 días aterrizaría en ese mes cada vez que entra desde el menú
  // —con la CierreMensualCard prefijada en él—. Ver docs/filtros-persistentes.md.
  // finanzas-empresa es MENSUAL (cierre mensual + atribución por mes de
  // check-in): el rango del enlace se ancla al MES del `from`.
  const desdeDeUrl = searchParams.get("from");
  const hastaDeUrl = searchParams.get("to");
  const mesDeUrl =
    desdeDeUrl && hastaDeUrl && dayjs(desdeDeUrl).isValid()
      ? dayjs(desdeDeUrl).startOf("month")
      : undefined;

  const [filtroFecha, setFiltroFecha] = useFiltroFechaPersistido(
    "finanzasEmpresa.periodo",
    { modo: "mes", mes: mesDeUrl ?? ahoraChile() },
    { restaurar: !mesDeUrl },
  );

  // La pestaña se recuerda: el admin que se queda trabajando en "Aseos" vuelve
  // a "Aseos". `?tab=` es un enlace directo (cards de /home, botones de otras
  // páginas) y manda sobre lo guardado.
  const tabDeUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useFiltroPersistido<string>(
    "finanzasEmpresa.pestana",
    tabDeUrl ?? "resumen",
    {
      validar: (v) =>
        typeof v === "string" && PESTANAS_FINANZAS_EMPRESA.includes(v),
      restaurar: !tabDeUrl,
    },
  );

  const categoriaInicial = searchParams.get("categoria") ?? undefined;

  // No hay efecto que lea la query string: `?tab=` y `?from=&to=` ya son el
  // valor inicial de la pestaña y del período. Aplicarlos con el setter los
  // dejaría guardados como preferencia del admin, que es justo lo que
  // `restaurar: false` está evitando.

  // Cada tab se envuelve en SectionErrorBoundary para que un crash de
  // render dentro de una sección NO desmonte toda la página de Finanzas
  // Empresa (lo que el usuario veía como "pantalla en blanco" al ordenar
  // arriendos por columna alfabética). FinanzasSection ya tiene su propio
  // boundary interno para Sueldos/Gastos, pero igual lo dejamos doble-
  // envuelto: el outer captura cualquier crash residual sin costo extra.
  const items: TabsProps["items"] = [
    {
      key: "resumen",
      label: "Resumen",
      children: (
        <SectionErrorBoundary titulo="Resumen">
          <ResumenSection filtroFecha={filtroFecha} refreshKey={refreshKey} />
        </SectionErrorBoundary>
      ),
    },
    {
      key: "sueldos",
      label: "Sueldos",
      children: (
        <SectionErrorBoundary titulo="Sueldos">
          <ReadOnlyBanner titulo="Sueldos" path="/sueldos" />
          <FinanzasSection
            titulo="Sueldos"
            data={sueldos}
            loading={loading}
            onCreate={createSueldo}
            onUpdate={updateSueldo}
            onDelete={deleteSueldo}
            usuarios={usuarios}
            propiedades={propiedades}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
            readOnly
          />
        </SectionErrorBoundary>
      ),
    },
    {
      // Mantenemos key="gastos" para compat con bookmarks (?tab=gastos)
      // pero la label visible y el banner apuntan a /flujos — el módulo
      // se renombró en el round 2026-05-13. Internamente FinanzasSection
      // sigue recibiendo titulo="Gastos" para no romper checks ya
      // existentes (`titulo === "Gastos"` se usa en columnas y modal).
      key: "gastos",
      label: "Flujos",
      children: (
        <SectionErrorBoundary titulo="Flujos">
          <ReadOnlyBanner titulo="Flujos" path="/flujos" />
          <FinanzasSection
            titulo="Gastos"
            data={gastos}
            loading={loading}
            onCreate={createGasto}
            onUpdate={updateGasto}
            onDelete={deleteGasto}
            usuarios={usuarios}
            propiedades={propiedades}
            filtroFecha={filtroFecha}
            categoriaInicial={categoriaInicial}
            refreshKey={refreshKey}
            readOnly
          />
        </SectionErrorBoundary>
      ),
    },
    {
      key: "aseos",
      label: "Aseos",
      children: (
        <SectionErrorBoundary titulo="Aseos">
          <AseosSection
            data={aseos}
            loading={loading}
            usuarios={usuarios}
            propiedades={propiedades}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
          />
        </SectionErrorBoundary>
      ),
    },
    {
      key: "lavanderia",
      label: "Lavandería",
      children: (
        <SectionErrorBoundary titulo="Lavandería">
          <LavanderiaSection
            propiedades={propiedades}
            usuarios={usuarios}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
          />
        </SectionErrorBoundary>
      ),
    },
    {
      key: "arriendos",
      label: "Arriendos",
      children: (
        <SectionErrorBoundary titulo="Arriendos">
          <ReadOnlyBanner titulo="Arriendos" path="/arriendos" />
          <ArriendosSection
            clavePersistencia="finanzasEmpresa.arriendos"
            data={arriendos}
            loading={loading}
            propiedades={propiedades}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
            // KPI cards por sector arriba de la tabla — vista de reporte.
            // En /arriendos no se muestran (esa página es operativa).
            showKpiBySector
          />
        </SectionErrorBoundary>
      ),
    },
    {
      key: "productos",
      label: "Productos",
      children: (
        <SectionErrorBoundary titulo="Productos">
          <ReadOnlyBanner titulo="Inventario" path="/inventario" />
          <ProductosSection
            data={productos}
            loading={loading}
            marcas={marcas}
            bodegas={bodegas}
            bodeguitas={bodeguitas}
            propiedades={propiedades}
            filtroFecha={filtroFecha}
            refreshKey={refreshKey}
          />
        </SectionErrorBoundary>
      ),
    },
  ];

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="finanzas-page">
        <PageHeader
          title="Finanzas de la empresa"
          helpText="Resumen financiero de la empresa: arriendos, aseos, lavandería y flujos operacionales."
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {/* ✅ controlado. `monthlyOnly`: finanzas-empresa se cierra y agrupa
              MES A MES (atribución por mes de check-in), así que el selector es
              solo mensual — sin "Desde/Hasta". Ver docs/flujo-financiero.md §1. */}
          <FiltroGlobalFecha
            value={filtroFecha}
            onChange={setFiltroFecha}
            monthlyOnly
          />
          {/* Recalcula todos los totales del período actual SIN cambiar el mes
              ni la pestaña. Útil tras crear/editar datos en otro módulo (o en
              otra pestaña de esta misma vista) para ver las cifras al día. */}
          <Tooltip title="Vuelve a calcular todos los totales del mes seleccionado sin cambiar el mes ni la pestaña que estás viendo">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Actualizar
            </Button>
          </Tooltip>
        </div>

        {/* Control DESTACADO de publicación al cliente (cierre mensual). Vive
            acá arriba —no dentro del tab Arriendos— porque publica el MES
            COMPLETO (todos los flujos), no solo arriendos. Ver
            docs/flujo-financiero.md §13. */}
        <CierreMensualCard
          propiedades={propiedades}
          defaultMonth={filtroFecha.mes ?? undefined}
        />

        {/* ✅ tabs controlado */}
        <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />
      </div>
      {/* Footer */}
    </Layout>
  );
}
